import { useMemo } from "react";
import { useDemoStore, type Role } from "./useDemoStore";
import type { Element as StandardElement, Instrumento, GroupSession } from "@/data";
import type { Evaluation, Dictamen, Rule, PrechecklistItem, PrechecklistState, PracticalScore, AttendanceBySession, GroupMaterial, CandidateObservation, ExamAvailability, LmsRoute, LmsProgressState, PreopChecklistRun } from "@/data/seed";

// Sentinelas estables: usar una constante module-level evita crear un []
// nuevo en cada selector run, lo que dispara "Maximum update depth exceeded"
// en useSyncExternalStore cuando la clave no está sembrada.
const EMPTY_ELEMENTS: StandardElement[] = [];
const EMPTY_INSTRUMENTS: Instrumento[] = [];
const EMPTY_RULES: Rule[] = [];
const EMPTY_ARR: never[] = [];
const EMPTY_SCORES: Record<string, PracticalScore> = {};
const EMPTY_ATTENDANCE: AttendanceBySession = {};
const EMPTY_PROGRESS: Record<string, boolean> = {};
const DEFAULT_EXAM_AVAILABILITY: ExamAvailability = { diagnostico: true, teorico: true };
const DEFAULT_LMS_PROGRESS: LmsProgressState = { attemptsByModule: {}, completedModuleIds: [] };

// ----- Readers (selectors) -----
export function useStandards() {
  return useDemoStore((s) => s.standards);
}
export function useStandardsList() {
  const standards = useStandards();
  return useMemo(() => Object.values(standards), [standards]);
}
export function useStandard(codigo: string | undefined) {
  return useDemoStore((s) => (codigo ? s.standards[codigo] : undefined));
}
export function useElementsForStandard(codigo: string | undefined) {
  return useDemoStore((s) => (codigo ? s.elementsByStandard[codigo] ?? EMPTY_ELEMENTS : EMPTY_ELEMENTS));
}
export function useInstrumentsForStandard(codigo: string | undefined) {
  return useDemoStore((s) => (codigo ? s.instrumentsByStandard[codigo] ?? EMPTY_INSTRUMENTS : EMPTY_INSTRUMENTS));
}
export function useRulesForStandard(codigo: string | undefined) {
  return useDemoStore((s) => (codigo ? s.rulesByStandard[codigo] ?? EMPTY_RULES : EMPTY_RULES));
}
export function useGroups() {
  return useDemoStore((s) => s.groups);
}
export function useGroupsList() {
  const groups = useGroups();
  return useMemo(() => Object.values(groups), [groups]);
}
export function useGroup(id: string | undefined) {
  return useDemoStore((s) => (id ? s.groups[id] : undefined));
}
export function useSessionsByGroup() {
  return useDemoStore((s) => s.sessionsByGroup);
}
export function useSessionsForGroup(grupoId: string | undefined) {
  return useDemoStore((s) => (grupoId ? s.sessionsByGroup[grupoId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useAllSessions(): GroupSession[] {
  const byGroup = useSessionsByGroup();
  return useMemo(() => Object.values(byGroup).flat(), [byGroup]);
}
export function useCandidates() {
  return useDemoStore((s) => s.candidates);
}
export function useCandidatesList() {
  const cands = useCandidates();
  return useMemo(() => Object.values(cands), [cands]);
}
export function useCandidate(id: string | undefined) {
  return useDemoStore((s) => (id ? s.candidates[id] : undefined));
}
export function useExpedientes() {
  return useDemoStore((s) => s.expedientes);
}
export function useExpedientesList() {
  const exps = useExpedientes();
  return useMemo(() => Object.values(exps), [exps]);
}
export function useExpediente(id: string | undefined) {
  return useDemoStore((s) => (id ? s.expedientes[id] : undefined));
}
export function useEvaluations() {
  return useDemoStore((s) => s.evaluations);
}
export function useEvaluationsList() {
  const ev = useEvaluations();
  return useMemo(() => Object.values(ev), [ev]);
}
export function useDictamenes() {
  return useDemoStore((s) => s.dictamenes);
}
export function useDictamenesList() {
  const d = useDictamenes();
  return useMemo(() => Object.values(d), [d]);
}
export function useUsers() {
  return useDemoStore((s) => s.users);
}
export function useUsersList() {
  const users = useUsers();
  return useMemo(() => Object.values(users), [users]);
}
export function useUsersByRole(role: Role) {
  const list = useUsersList();
  return useMemo(() => list.filter((u) => u.roles.includes(role) && u.estado === "activo"), [list, role]);
}
export function useAuditLog() {
  return useDemoStore((s) => s.auditLog);
}
export function useAppMode() {
  return useDemoStore((s) => s.settings.mode);
}
export function useEffectiveUser() {
  const id = useDemoStore((s) => s.settings.effectiveUserId);
  return useDemoStore((s) => (id ? s.users[id] : undefined));
}
export function useEnrollmentsForCandidate(candidatoId: string | undefined) {
  return useDemoStore((s) => (candidatoId ? s.enrollments[candidatoId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useExpedienteDocs(expedienteId: string | undefined) {
  return useDemoStore((s) => (expedienteId ? s.expedienteDocs[expedienteId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useExamAttemptsForExpediente(expedienteId: string | undefined) {
  return useDemoStore((s) => (expedienteId ? s.examAttempts[expedienteId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useExamAttemptsForCandidate(candidatoId: string | undefined) {
  const all = useDemoStore((s) => s.examAttempts);
  return useMemo(() => {
    if (!candidatoId) return [];
    return Object.values(all).flat().filter((a) => a.candidatoId === candidatoId);
  }, [all, candidatoId]);
}
export function useDiagnosticBank(codigo: string | undefined) {
  const explicit = useDemoStore((s) => (codigo ? s.diagnosticBankByStandard[codigo] ?? EMPTY_ARR : EMPTY_ARR));
  const instruments = useDemoStore((s) => (codigo ? s.instrumentsByStandard[codigo] ?? EMPTY_INSTRUMENTS : EMPTY_INSTRUMENTS));
  return useMemo(() => {
    if (explicit.length > 0) return explicit;
    // Fallback: derive from instruments edited by the admin (observación + checklist).
    const derived: { id: string; area: string; pregunta: string }[] = [];
    for (const ins of instruments) {
      if (ins.tipo !== "observación" && ins.tipo !== "checklist") continue;
      for (const it of ins.items ?? []) {
        derived.push({ id: `derived-${ins.id}-${it.id}`, area: ins.nombre, pregunta: it.texto });
      }
    }
    return derived;
  }, [explicit, instruments]);
}
export function useTheoreticalBank(codigo: string | undefined) {
  const explicit = useDemoStore((s) => (codigo ? s.theoreticalBankByStandard[codigo] ?? EMPTY_ARR : EMPTY_ARR));
  const instruments = useDemoStore((s) => (codigo ? s.instrumentsByStandard[codigo] ?? EMPTY_INSTRUMENTS : EMPTY_INSTRUMENTS));
  return useMemo(() => {
    if (explicit.length > 0) return explicit;
    // Fallback: derive from "examen" instrument items edited by the admin.
    // Items don't carry options, so we present a Verdadero/Falso pair; admin can later
    // promote these into a richer multi-option bank via setTheoreticalBank.
    const derived: { id: string; enunciado: string; opciones: string[]; correcta: number }[] = [];
    for (const ins of instruments) {
      if (ins.tipo !== "examen") continue;
      for (const it of ins.items ?? []) {
        derived.push({
          id: `derived-${ins.id}-${it.id}`,
          enunciado: it.texto,
          opciones: ["Verdadero", "Falso"],
          correcta: 0,
        });
      }
    }
    return derived;
  }, [explicit, instruments]);
}
export function usePrechecklistTemplate(codigo: string | undefined): PrechecklistItem[] {
  return useDemoStore((s) => (codigo ? s.prechecklistTemplateByStandard[codigo] ?? EMPTY_ARR : EMPTY_ARR));
}
export function usePrechecklistForExpediente(expedienteId: string | undefined): PrechecklistState | undefined {
  return useDemoStore((s) => (expedienteId ? s.prechecklistByExpediente[expedienteId] : undefined));
}
export function usePracticalScoresForExpediente(expedienteId: string | undefined): Record<string, PracticalScore> {
  return useDemoStore((s) => (expedienteId ? s.practicalScoresByExpediente[expedienteId] ?? EMPTY_SCORES : EMPTY_SCORES));
}
export function usePracticalNotesForExpediente(expedienteId: string | undefined): string {
  return useDemoStore((s) => (expedienteId ? s.practicalNotesByExpediente[expedienteId] ?? "" : ""));
}
export function useExpedientesForEvaluator(evaluadorId: string | undefined) {
  const groups = useGroupsList();
  const exps = useExpedientesList();
  return useMemo(() => {
    if (!evaluadorId) return [] as { exp: typeof exps[number]; grupoId: string }[];
    const myGroups = groups.filter((g) => (g.evaluadoresIds ?? [g.evaluadorId]).includes(evaluadorId));
    const candToGroup = new Map<string, string>();
    for (const g of myGroups) for (const cid of g.candidatosIds) if (!candToGroup.has(cid)) candToGroup.set(cid, g.id);
    return exps
      .filter((e) => candToGroup.has(e.candidatoId))
      .map((exp) => ({ exp, grupoId: candToGroup.get(exp.candidatoId) ?? "" }));
  }, [groups, exps, evaluadorId]);
}
export function useDictamenForExpediente(expedienteId: string | undefined): Dictamen | undefined {
  return useDemoStore((s) => (expedienteId ? s.dictamenes[`dict-${expedienteId}`] : undefined));
}
export function useGroupsForInstructor(instructorId: string | undefined) {
  const list = useGroupsList();
  return useMemo(() => (instructorId ? list.filter((g) => g.instructorId === instructorId) : []), [list, instructorId]);
}
/** Devuelve el ID del instructor activo: el usuario seleccionado en "ver como" si es instructor,
 *  o el primer instructor del sistema como fallback (igual que candidato usa candList[0]). */
export function useEffectiveInstructorId(): string | undefined {
  const effective = useEffectiveUser();
  const instructors = useUsersByRole("instructor");
  return effective?.roles.includes("instructor") ? effective.id : instructors[0]?.id;
}
export function useAttendanceForGroup(grupoId: string | undefined): AttendanceBySession {
  return useDemoStore((s) => (grupoId ? s.attendanceByGroup[grupoId] ?? EMPTY_ATTENDANCE : EMPTY_ATTENDANCE));
}
export function useProgressForExpediente(expedienteId: string | undefined): Record<string, boolean> {
  return useDemoStore((s) => (expedienteId ? s.progressByExpediente[expedienteId] ?? EMPTY_PROGRESS : EMPTY_PROGRESS));
}
export function useMaterialsForGroup(grupoId: string | undefined): GroupMaterial[] {
  return useDemoStore((s) => (grupoId ? s.materialsByGroup[grupoId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useObservationsForCandidate(candidatoId: string | undefined): CandidateObservation[] {
  return useDemoStore((s) => (candidatoId ? s.observationsByCandidate[candidatoId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useExamAvailability(expedienteId: string | undefined): ExamAvailability {
  return useDemoStore((s) => {
    if (!expedienteId) return DEFAULT_EXAM_AVAILABILITY;
    return s.examAvailabilityByExpediente[expedienteId] ?? DEFAULT_EXAM_AVAILABILITY;
  });
}
// ----- LMS Ruta formativa -----
export function useLmsRoute(codigo: string | undefined): LmsRoute | undefined {
  return useDemoStore((s) => (codigo ? s.lmsRouteByStandard[codigo] : undefined));
}
export function useLmsProgress(expedienteId: string | undefined): LmsProgressState {
  return useDemoStore((s) => {
    if (!expedienteId) return DEFAULT_LMS_PROGRESS;
    return s.lmsProgressByExpediente[expedienteId] ?? DEFAULT_LMS_PROGRESS;
  });
}
export function usePreopRuns(expedienteId: string | undefined): PreopChecklistRun[] {
  return useDemoStore((s) => (expedienteId ? s.preopRunsByExpediente[expedienteId] ?? EMPTY_ARR : EMPTY_ARR));
}
export function useLmsAvancePct(expedienteId: string | undefined, codigo: string | undefined): number {
  const route = useLmsRoute(codigo);
  const progress = useLmsProgress(expedienteId);
  return useMemo(() => {
    if (!route || route.modules.length === 0) return 0;
    return Math.round((progress.completedModuleIds.length / route.modules.length) * 100);
  }, [route, progress]);
}

export function useExpedienteForCandidateStandard(candidatoId: string | undefined, codigo: string | undefined) {
  const exps = useExpedientes();
  return useMemo(() => {
    if (!candidatoId || !codigo) return undefined;
    return Object.values(exps).find((e) => e.candidatoId === candidatoId && e.estandar === codigo);
  }, [exps, candidatoId, codigo]);
}

// ----- CRUD hooks -----
export function useStandardsCrud() {
  return {
    add: useDemoStore((s) => s.addStandard),
    update: useDemoStore((s) => s.updateStandard),
    remove: useDemoStore((s) => s.removeStandard),
    restore: useDemoStore((s) => s.restoreStandard),
    publish: useDemoStore((s) => s.publishStandard),
    bulkImport: (items: Parameters<ReturnType<typeof useDemoStore.getState>["bulkImport"]>[1]) =>
      useDemoStore.getState().bulkImport("standards", items as any),
  };
}
export function useElementsCrud(codigo: string) {
  return {
    setAll: useDemoStore((s) => s.setElementsForStandard).bind(null, codigo),
    upsert: useDemoStore((s) => s.upsertElement).bind(null, codigo),
    remove: useDemoStore((s) => s.removeElement).bind(null, codigo),
    upsertCriterio: useDemoStore((s) => s.upsertCriterio).bind(null, codigo),
    removeCriterio: useDemoStore((s) => s.removeCriterio).bind(null, codigo),
  };
}
export function useInstrumentsCrud(codigo: string) {
  return {
    setAll: useDemoStore((s) => s.setInstrumentsForStandard).bind(null, codigo) as (instruments: Instrumento[]) => void,
    upsert: useDemoStore((s) => s.upsertInstrument).bind(null, codigo) as (i: Instrumento) => void,
    remove: useDemoStore((s) => s.removeInstrument).bind(null, codigo) as (id: string) => void,
  };
}
export function useRulesCrud(codigo: string) {
  return {
    setAll: useDemoStore((s) => s.setRulesForStandard).bind(null, codigo) as (rules: Rule[]) => void,
    upsert: useDemoStore((s) => s.upsertRule).bind(null, codigo) as (r: Rule) => void,
    remove: useDemoStore((s) => s.removeRule).bind(null, codigo) as (id: string) => void,
  };
}
export function useGroupsCrud() {
  return {
    add: useDemoStore((s) => s.addGroup),
    update: useDemoStore((s) => s.updateGroup),
    remove: useDemoStore((s) => s.removeGroup),
    bulkImport: (items: any[]) => useDemoStore.getState().bulkImport("groups", items as any),
  };
}
export function useSessionsCrud(grupoId: string) {
  return {
    setAll: useDemoStore((s) => s.setSessionsForGroup).bind(null, grupoId) as (sessions: GroupSession[]) => void,
    upsert: useDemoStore((s) => s.upsertSession).bind(null, grupoId) as (s: GroupSession) => void,
    remove: useDemoStore((s) => s.removeSession).bind(null, grupoId) as (id: string) => void,
  };
}
export function useCandidatesCrud() {
  return {
    add: useDemoStore((s) => s.addCandidate),
    update: useDemoStore((s) => s.updateCandidate),
    remove: useDemoStore((s) => s.removeCandidate),
    bulkImport: (items: any[]) => useDemoStore.getState().bulkImport("candidates", items as any),
  };
}
export function useExpedientesCrud() {
  return {
    upsert: useDemoStore((s) => s.upsertExpediente),
    update: useDemoStore((s) => s.updateExpediente),
    remove: useDemoStore((s) => s.removeExpediente),
    bulkImport: (items: any[]) => useDemoStore.getState().bulkImport("expedientes", items as any),
  };
}
export function useEvaluationsCrud() {
  return {
    add: useDemoStore((s) => s.addEvaluation) as (e: Evaluation) => void,
    update: useDemoStore((s) => s.updateEvaluation),
    remove: useDemoStore((s) => s.removeEvaluation),
    bulkImport: (items: any[]) => useDemoStore.getState().bulkImport("evaluations", items as any),
  };
}
export function useDictamenesCrud() {
  return {
    add: useDemoStore((s) => s.addDictamen) as (d: Dictamen) => void,
    update: useDemoStore((s) => s.updateDictamen),
    remove: useDemoStore((s) => s.removeDictamen),
    bulkImport: (items: any[]) => useDemoStore.getState().bulkImport("dictamenes", items as any),
  };
}
export function useUsersCrud() {
  return {
    add: useDemoStore((s) => s.addUser),
    update: useDemoStore((s) => s.updateUser),
    remove: useDemoStore((s) => s.removeUser),
    bulkImport: (items: any[]) => useDemoStore.getState().bulkImport("users", items as any),
  };
}
